import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Modal } from "@/components/ui/modal";
import { useContactAttributeDefinitionStore } from "@/store/contactAttributeDefinitionStore";
import { useAuthStore } from "@/store/authStore";
import type {
  AttributeDataType,
  ContactAttributeDefinition,
  CreateAttributeDefinitionPayload,
  UpdateAttributeDefinitionPayload,
} from "@/types/contacts";

const DATA_TYPE_LABELS: Record<AttributeDataType, string> = {
  String: "Text",
  Number: "Number",
  Date: "Date",
  Boolean: "Yes / No",
  Json: "JSON",
};

const DATA_TYPE_COLORS: Record<AttributeDataType, string> = {
  String: "bg-muted text-muted-foreground",
  Number: "bg-blue-500/10 text-blue-500",
  Date: "bg-purple-500/10 text-purple-600",
  Boolean: "bg-green-500/10 text-green-600",
  Json: "bg-orange-500/10 text-orange-600",
};

function toSnakeCase(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

interface AttributeFormProps {
  accountId: string;
  existing?: ContactAttributeDefinition;
  onClose: () => void;
}

function AttributeDefinitionModal({ accountId, existing, onClose }: AttributeFormProps) {
  const { createDefinition, updateDefinition, isSaving, error } = useContactAttributeDefinitionStore();
  const [label, setLabel] = useState(existing?.label ?? "");
  const [key, setKey] = useState(existing?.key ?? "");
  const [keyTouched, setKeyTouched] = useState(!!existing);
  const [dataType, setDataType] = useState<AttributeDataType>(existing?.dataType ?? "String");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [isRequired, setIsRequired] = useState(existing?.isRequired ?? false);
  const [defaultValue, setDefaultValue] = useState(existing?.defaultValue ?? "");

  const handleLabelChange = (value: string) => {
    setLabel(value);
    if (!keyTouched && !existing) {
      setKey(toSnakeCase(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || (!existing && !key.trim())) return;
    try {
      if (existing) {
        const data: UpdateAttributeDefinitionPayload = {
          label: label.trim(),
          description: description.trim() || undefined,
          isRequired,
          defaultValue: defaultValue.trim() || undefined,
        };
        await updateDefinition(accountId, existing.id, data);
      } else {
        const data: CreateAttributeDefinitionPayload = {
          key: key.trim(),
          label: label.trim(),
          dataType,
          description: description.trim() || undefined,
          isRequired,
          defaultValue: defaultValue.trim() || undefined,
        };
        await createDefinition(accountId, data);
      }
      onClose();
    } catch {
      // error shown from store
    }
  };

  const inputClass = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Label <span className="text-destructive">*</span>
        </label>
        <input
          value={label}
          onChange={(e) => handleLabelChange(e.target.value)}
          required
          placeholder="e.g. Favorite Car"
          className={inputClass}
          autoFocus
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Key <span className="text-destructive">*</span>
          <span className="ml-2 text-xs text-muted-foreground font-normal">used in API / imports</span>
        </label>
        {existing ? (
          <div className={`${inputClass} bg-muted text-muted-foreground cursor-not-allowed font-mono`}>
            {existing.key}
          </div>
        ) : (
          <input
            value={key}
            onChange={(e) => { setKey(e.target.value); setKeyTouched(true); }}
            required
            pattern="^[a-zA-Z0-9_]+$"
            title="Only letters, numbers, and underscores"
            placeholder="e.g. favorite_car"
            className={`${inputClass} font-mono`}
          />
        )}
        {!existing && <p className="mt-1 text-xs text-muted-foreground">Letters, numbers, and underscores only. Cannot be changed after creation.</p>}
      </div>
      {!existing && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Type <span className="text-destructive">*</span>
          </label>
          <select
            value={dataType}
            onChange={(e) => setDataType(e.target.value as AttributeDataType)}
            className={inputClass}
          >
            {(Object.keys(DATA_TYPE_LABELS) as AttributeDataType[]).map((t) => (
              <option key={t} value={t}>{DATA_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Description</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional hint shown to editors"
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Default value</label>
        <input
          value={defaultValue}
          onChange={(e) => setDefaultValue(e.target.value)}
          placeholder="Leave blank for none"
          className={inputClass}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
        <input
          type="checkbox"
          checked={isRequired}
          onChange={(e) => setIsRequired(e.target.checked)}
          className="rounded border-border"
        />
        Required field
      </label>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
        >
          Cancel
        </button>
        <Button type="submit" disabled={isSaving || !label.trim() || (!existing && !key.trim())}>
          {isSaving ? (existing ? "Saving…" : "Creating…") : (existing ? "Save changes" : "Create attribute")}
        </Button>
      </div>
    </form>
  );
}

export default function ContactAttributeDefinitionsPage() {
  const user = useAuthStore((s) => s.user);
  const accountId = user?.currentAccountId ?? user?.ownedAccountId ?? "";

  const { definitions, isLoading, isSaving, fetchDefinitions, deleteDefinition } = useContactAttributeDefinitionStore();

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ContactAttributeDefinition | null>(null);
  const [deleting, setDeleting] = useState<ContactAttributeDefinition | null>(null);

  useEffect(() => {
    if (accountId) fetchDefinitions(accountId);
  }, [accountId, fetchDefinitions]);

  const handleDelete = async () => {
    if (!deleting || !accountId) return;
    await deleteDefinition(accountId, deleting.id);
    setDeleting(null);
  };

  return (
    <ProtectedRoute>
      <div className="lw-contact-attr-defs max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Contact Attributes</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Define custom fields that appear on every contact's profile.{" "}
              <Link to="/audience/contacts" className="text-primary hover:underline">Back to contacts</Link>
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)}>New attribute</Button>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
            Loading…
          </div>
        ) : definitions.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-lg">
            <p className="text-muted-foreground text-sm mb-3">No custom attributes defined yet.</p>
            <Button onClick={() => setShowCreate(true)}>Add your first attribute</Button>
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Label</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Key</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Required</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {definitions.map((def) => (
                  <tr key={def.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground">
                      {def.label}
                      {def.description && (
                        <p className="text-xs text-muted-foreground font-normal mt-0.5">{def.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {def.key}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${DATA_TYPE_COLORS[def.dataType]}`}>
                        {DATA_TYPE_LABELS[def.dataType]}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">
                      {def.isRequired ? (
                        <span className="text-foreground font-medium">Yes</span>
                      ) : "No"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditing(def)}
                          className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-accent transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleting(def)}
                          className="text-xs text-destructive hover:text-destructive/80 px-2 py-1 rounded hover:bg-destructive/10 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={showCreate} title="New attribute" size="md" onClose={() => setShowCreate(false)}>
        <AttributeDefinitionModal accountId={accountId} onClose={() => setShowCreate(false)} />
      </Modal>

      <Modal isOpen={!!editing} title="Edit attribute" size="md" onClose={() => setEditing(null)}>
        {editing && (
          <AttributeDefinitionModal accountId={accountId} existing={editing} onClose={() => setEditing(null)} />
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => { if (!open) setDeleting(null); }}
        title="Delete attribute"
        description={deleting ? `Delete "${deleting.label}"? This will not remove existing values already stored on contacts.` : ""}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        variant="destructive"
      />
    </ProtectedRoute>
  );
}
